import { apiClient } from '../client'
import type {
  EmployeeEmploymentStatus,
  SystemEmployee,
  SystemPermission,
  SystemRole,
  UpsertSystemEmployeeRequest,
  UpsertSystemPermissionRequest,
  UpsertSystemRoleRequest,
} from '../types/system'
import type {
  SystemDictionary,
  SystemDictionaryItem,
  UpsertSystemDictionaryItemRequest,
  UpsertSystemDictionaryRequest,
} from '../types/dictionary'

export async function getSystemEmployees(params: {
  keyword?: string
  status?: EmployeeEmploymentStatus
} = {}): Promise<SystemEmployee[]> {
  const search = new URLSearchParams()
  if (params.keyword) search.set('keyword', params.keyword)
  if (params.status) search.set('status', params.status)
  const suffix = search.size ? `?${search.toString()}` : ''
  return apiClient.get<SystemEmployee[]>(`/api/system/employees${suffix}`)
}

export function createSystemEmployee(data: UpsertSystemEmployeeRequest) {
  return apiClient.post<SystemEmployee>('/api/system/employees', data)
}

export function updateSystemEmployee(id: number, data: UpsertSystemEmployeeRequest) {
  return apiClient.put<SystemEmployee>(`/api/system/employees/${id}`, data)
}

export function replaceEmployeeRoles(id: number, roleIds: number[]) {
  return apiClient.put<SystemEmployee>(`/api/system/employees/${id}/roles`, { roleIds })
}

export function getSystemRoles(): Promise<SystemRole[]> {
  return apiClient.get<SystemRole[]>('/api/system/roles')
}

export function createSystemRole(data: UpsertSystemRoleRequest) {
  return apiClient.post<SystemRole>('/api/system/roles', data)
}

export function updateSystemRole(id: number, data: UpsertSystemRoleRequest) {
  return apiClient.put<SystemRole>(`/api/system/roles/${id}`, data)
}

export function replaceRolePermissions(id: number, permissionIds: number[]) {
  return apiClient.put<SystemRole>(`/api/system/roles/${id}/permissions`, { permissionIds })
}

export function getSystemPermissions(): Promise<SystemPermission[]> {
  return apiClient.get<SystemPermission[]>('/api/system/permissions')
}

export function createSystemPermission(data: UpsertSystemPermissionRequest) {
  return apiClient.post<SystemPermission>('/api/system/permissions', data)
}

export function updateSystemPermission(id: number, data: UpsertSystemPermissionRequest) {
  return apiClient.put<SystemPermission>(`/api/system/permissions/${id}`, data)
}

export async function getSystemDictionaries(params: { keyword?: string; enabled?: boolean } = {}): Promise<SystemDictionary[]> {
  const search = new URLSearchParams()
  if (params.keyword) search.set('keyword', params.keyword)
  if (params.enabled !== undefined) search.set('enabled', String(params.enabled))
  const suffix = search.size ? `?${search.toString()}` : ''
  return apiClient.get<SystemDictionary[]>(`/api/system/dictionaries${suffix}`)
}

export function createSystemDictionary(data: UpsertSystemDictionaryRequest) {
  return apiClient.post<SystemDictionary>('/api/system/dictionaries', data)
}

export function updateSystemDictionary(id: number, data: UpsertSystemDictionaryRequest) {
  return apiClient.put<SystemDictionary>(`/api/system/dictionaries/${id}`, data)
}

export function getSystemDictionaryItems(dictionaryId: number): Promise<SystemDictionaryItem[]> {
  return apiClient.get<SystemDictionaryItem[]>(`/api/system/dictionaries/${dictionaryId}/items`)
}

export function createSystemDictionaryItem(dictionaryId: number, data: UpsertSystemDictionaryItemRequest) {
  return apiClient.post<SystemDictionaryItem>(`/api/system/dictionaries/${dictionaryId}/items`, data)
}

export function updateSystemDictionaryItem(dictionaryId: number, itemId: number, data: UpsertSystemDictionaryItemRequest) {
  return apiClient.put<SystemDictionaryItem>(`/api/system/dictionaries/${dictionaryId}/items/${itemId}`, data)
}
